import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, gql } from '@apollo/client';
import { Image, ImageViewer, Skeleton } from 'antd-mobile';
import { PackageEvent } from '../graphql/queries/getPackageDetail';
import { formatTimestampToSecondsTime } from '../../../utils/dateTimeUtils';
import { useDspStatusInternationalization } from '../../../hooks/useDspStatusInternationalization';
import {formatWeightKgToLb} from "../../../utils/numberUtils";
import {getCurrentLanguage, getLocalizedText} from "../../../utils/i18nUtils";
import {getOrderNoteContent} from "../../../utils/orderNoteUtils";
import {maskPhoneNumber} from "../../../utils/phoneUtils";
import {
    GET_DELIVERY_METHOD_I18N_CONFIG,
    GetDeliveryMethodI18nConfigResponse
} from '../../../graphql/queries/getDeliveryMethodI18nConfig';

// 批量异常字典查询 - 使用完整版接口
const GET_ALL_EXCEPTION_DICTIONARIES = gql`
  query GetAllExceptionDictionariesFull {
    getAllExceptionDictionariesFull {
      exceptionReasonCode
      primaryType
      secondaryTypeZh
      secondaryTypeEn
      secondaryTypeEs
      actionDescription
      isUploadsImages
      exceptionSubReasons {
        code
        textZh
        textEn
        textEs
      }
    }
  }
`;

interface ExceptionSubReasonType {
    code: string;
    textZh: string;
    textEn: string;
    textEs: string;
}

interface ExceptionDictionary {
    exceptionReasonCode: string;
    primaryType: string;
    secondaryTypeZh: string;
    secondaryTypeEn: string;
    secondaryTypeEs: string;
    actionDescription: string;
    isUploadsImages: boolean;
    exceptionSubReasons?: ExceptionSubReasonType[];
}

interface ExceptionDictionariesResponse {
    getAllExceptionDictionariesFull: ExceptionDictionary[];
}

interface DspPackageTimelineProps {
    packageEvents: PackageEvent[];
    channelName?: string;
}

const DspPackageTimeline: React.FC<DspPackageTimelineProps> = ({ packageEvents, channelName }) => {
    const { t, i18n } = useTranslation();
    const { getNextActionTextFromBackend } = useDspStatusInternationalization();
    const [exceptionDictMap, setExceptionDictMap] = useState<Map<string, ExceptionDictionary>>(new Map());
    const [imageViewerVisible, setImageViewerVisible] = useState(false);
    const [currentImages, setCurrentImages] = useState<string[]>([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const currentLanguage = getCurrentLanguage();

    const { data: deliveryMethodI18nData } = useQuery<GetDeliveryMethodI18nConfigResponse>(GET_DELIVERY_METHOD_I18N_CONFIG, { fetchPolicy: 'cache-first' });
    const deliveryMethodI18nConfig = deliveryMethodI18nData?.getDeliveryMethodI18nConfig ?? [];
    
    // 批量异常字典查询hook - 改为useQuery自动加载
    const { data: exceptionDictData, loading: exceptionDictLoading } = useQuery<ExceptionDictionariesResponse>(
        GET_ALL_EXCEPTION_DICTIONARIES,
        {
            fetchPolicy: 'cache-first',
            errorPolicy: 'all'
        }
    );
    
    // 二级原因 code -> 文案 的扁平映射（从所有异常字典的 exceptionSubReasons 聚合）
    const subReasonTextMap = useMemo(() => {
        const map = new Map<string, ExceptionSubReasonType>();
        exceptionDictData?.getAllExceptionDictionariesFull?.forEach(dict => {
            dict.exceptionSubReasons?.forEach(sub => {
                map.set(sub.code, sub);
            });
        });
        return map;
    }, [exceptionDictData]);

    // 当异常字典数据加载完成时，更新本地状态
    useEffect(() => {
        if (exceptionDictData?.getAllExceptionDictionariesFull) {
            const dictMap = new Map<string, ExceptionDictionary>();
            exceptionDictData.getAllExceptionDictionariesFull.forEach(dict => {
                dictMap.set(dict.exceptionReasonCode, dict);
            });
            setExceptionDictMap(dictMap);
        }
    }, [exceptionDictData]);

    // 获取二级异常原因文本
    const getExceptionSubReasonText = useCallback((code: string): string => {
        const sub = subReasonTextMap.get(code);
        if (!sub) return code;
        return getLocalizedText({ zh: sub.textZh, en: sub.textEn, es: sub.textEs }, sub.textEn);
    }, [subReasonTextMap]);
    
    // 获取异常原因文本（一级）
    const getExceptionReasonText = useCallback((exceptionReasonCode: string): string => {
        if (!exceptionReasonCode) return '';
        
        const exception = exceptionDictMap.get(exceptionReasonCode);
        if (!exception) return exceptionReasonCode;

        const currentLang = localStorage.getItem('i18nextLng') || 'en';

        // 使用包含方式匹配语言，支持不同格式的语言代码
        if (currentLang.includes('zh')) {
            return exception.secondaryTypeZh || exceptionReasonCode;
        } else if (currentLang.includes('es')) {
            return exception.secondaryTypeEs || exceptionReasonCode;
        } else {
            return exception.secondaryTypeEn || exceptionReasonCode;
        }
    }, [exceptionDictMap]);

    // 处理图片点击预览
    const handleImageClick = useCallback((images: string[], index: number) => {
        setCurrentImages(images);
        setCurrentImageIndex(index);
        setImageViewerVisible(true);
    }, []);

    // 格式化事件标题
    const formatEventTitle = useCallback((eventType: string): string => {
        if (!eventType) return '-';
        
        // 移除 OrderEventData 前缀
        const cleanType = eventType.startsWith('OrderEventData') 
            ? eventType.substring(14) 
            : eventType;
        
        const translationKey = `enum.orderEvent.${cleanType}`;
        const translated = t(translationKey);
        
        return translated === translationKey ? cleanType : translated;
    }, [t]);

    // 格式化操作人信息
    const formatByInfo = useCallback((event: PackageEvent): string => {
        // 优先使用渠道信息
        if (event?.channelId && event?.channelApiVersion && channelName) {
            return `${t('components.timeline.by')} ${channelName}-V${event.channelApiVersion}`;
        }
        
        // 如果没有渠道信息，使用原有逻辑
        const details = event.details;
        if (!details || details.length === 0) return '-';
        
        const detail = details[0] || '-';
        if (detail.startsWith('by ')) {

            if (details.length > 1 && event.type === "OrderEventDataAssignedToDriver") {
                const formattedDetails = details.map(detail => {
                    if (detail.startsWith('by ')) {
                        return `${t('components.timeline.by')} ${detail.substring(3)}`;
                    } else if (detail.startsWith('to ')) {
                        return `${t('components.timeline.to')} ${detail.substring(3)}`;
                    } else {
                        return detail;
                    }
                });
                return formattedDetails.join(' ');
            }
            const content = detail.substring(3);
            return `${t('components.timeline.by')} ${content}`;
        }
        
        return detail;
    }, [t, channelName]);

    const formatAddress = useCallback((address: any): string => {
        if (!address) return '';
        const parts = [
            address.addressLine1,
            address.addressLine2,
            `${address.city}, ${address.state}, ${address.zipcode}, US`
        ].filter(Boolean);
        return parts.join('\n');
    }, []);

    const PREDEFINED_POD_REASONS = ['NO_LABEL_PHOTO', 'WRONG_TRK', 'NO_HOUSE_APT', 'WRONG_HOUSE_APT'];

    const formatPodComment = useCallback((podComment: string[] | undefined): string => {
        if (!podComment) return '';

        // 遍历数组中的每个评论项进行国际化处理
        const formattedComments = podComment.map(comment => {
            // 检查是否是预定义的原因代码
            if (PREDEFINED_POD_REASONS.includes(comment)) {
                return t(`components.timeline.reasons.${comment}`);
            }

            // 如果不是预定义代码，直接返回原值
            return comment;
        });

        // 用逗号连接多个评论
        return formattedComments.join(', ');
    }, [PREDEFINED_POD_REASONS]);

    // 时间轴项目
    const timelineItems = useMemo(() => {
        if (!packageEvents || packageEvents.length === 0) return [];

        return packageEvents.slice().reverse().map((event, index) => ({
            key: event.eventId || index,
            color: index === 0 ? 'purple' : 'gray',
            children: (
                <div className="space-y-2" key={event.eventId || index}>
                    {/* 事件标题和时间 */}
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1">
                        <span className="text-base font-medium break-words">
                            {formatEventTitle(event.type)}
                        </span>
                        <span className="text-sm text-gray-500 flex-shrink-0 whitespace-nowrap">
                            {event.time ? formatTimestampToSecondsTime(parseInt(event.time)) : '-'}
                        </span>
                    </div>
                    
                    {/* 对于OrderEventDataDriverChanged事件，先显示新司机信息，再显示操作人信息 */}
                    {event.type === 'OrderEventDataDriverChanged' && (event.driverName || event.driverPhone || event.dspName) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {event.driverName && (
                                <div>{`${t('components.timeline.newDriverName')}: ${event.driverName}`}</div>
                            )}
                            {event.driverPhone && (
                                <div>{`${t('components.timeline.newDriverPhone')}: ${maskPhoneNumber(event.driverPhone)}`}</div>
                            )}
                            {event.dspName && (
                                <div>{`${t('components.timeline.dspName')}: ${event.dspName}`}</div>
                            )}
                        </div>
                    )}

                    {/* 派件签收事件的特殊展示 */}
                    {event.type === 'OrderEventDataDelivered' && (event.driverName || event.driverPhone || event.dspName || event.deliveryDistance !== undefined || event.latitude !== undefined || event.longitude !== undefined || event.deliveryMethod) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {event.driverName && (
                                <div>{`${t('components.timeline.driverName')}: ${event.driverName}`}</div>
                            )}
                            {event.driverPhone && (
                                <div>{`${t('components.timeline.driverPhone')}: ${event.driverPhone}`}</div>
                            )}
                            {event.dspName && (
                                <div>{`${t('components.timeline.dspName')}: ${event.dspName}`}</div>
                            )}
                            {event.latitude !== undefined && event.latitude !== null && event.longitude !== undefined && event.longitude !== null && (
                                <div>{`${t('components.timeline.deliveryLocation')}: ${event.latitude === 0 && event.longitude === 0 ? '0,0' : `${event.latitude.toFixed(7)}, ${event.longitude.toFixed(7)}`}`}</div>
                            )}
                            {event.deliveryDistance !== undefined && event.deliveryDistance !== null &&
                             event.latitude !== undefined && event.latitude !== null && event.longitude !== undefined && event.longitude !== null &&
                             (event.latitude !== 0 || event.longitude !== 0) && (
                                <div>{`${t('components.timeline.deliveryDistance', { distance: event.deliveryDistance.toFixed(2) })} `}</div>
                            )}
                            {event.deliveryMethod && deliveryMethodI18nConfig.length > 0 && (() => {
                                const opt = deliveryMethodI18nConfig.find((c) => c.code === event.deliveryMethod);
                                const langKey = (i18n.language || '').toLowerCase().includes('zh') ? 'zh' : (i18n.language || '').toLowerCase().includes('es') ? 'es' : 'en';
                                const label = opt ? (opt[langKey as keyof typeof opt] || opt.en) || opt.code : event.deliveryMethod;
                                return <div>{`${t('components.timeline.deliveryMethod')}: ${label}`}</div>;
                            })()}
                        </div>
                    )}
                    
                    {/* 操作人信息 */}
                    <div className="text-gray-600 text-sm">
                        {formatByInfo(event)}
                    </div>
                    {/* 显示评论（如机审失败原因） */}
                    {event.podComment && (
                        <div className="text-gray-500 text-sm">
                            {`${t('components.timeline.podComment')}${formatPodComment(event.podComment)}`}
                        </div>
                    )}
                    {/* 失败原因 */}
                    {event.failReason && (
                        <div className="text-gray-600 text-sm">
                            {`${t('components.timeline.reason')}${event.failReason}`}
                        </div>
                    )}
                    
                    {/* 异常原因：有二级原因时展示一级+二级，否则只展示一级 */}
                    {(event.exceptionReasonCode || (event.exceptionSubReasons && event.exceptionSubReasons.length > 0)) && (
                        <div className="text-gray-600 text-sm">
                            {`${t('components.timeline.reason')}${
                                event.exceptionSubReasons && event.exceptionSubReasons.length > 0
                                    ? (event.exceptionReasonCode ? `${getExceptionReasonText(event.exceptionReasonCode)} - ` : '') +
                                      event.exceptionSubReasons.map(code => getExceptionSubReasonText(code)).join(', ')
                                    : getExceptionReasonText(event.exceptionReasonCode!)
                            }`}
                        </div>
                    )}
                    
                    {/* LogScan事件的消息键国际化 */}
                    {event.messageKey && (
                        <div className="text-gray-600 text-sm">
                            {`${t('components.timeline.reason')}${t(event.messageKey)}`}
                        </div>
                    )}
                    
                    {/* LogScan事件的分拣机编码 */}
                    {event.sortingMachineCode && (
                        <div className="text-gray-600 text-sm">
                            {`${t('components.timeline.sortingMachineCode')}: ${event.sortingMachineCode}`}
                        </div>
                    )}
                    
                    {/* LogScan事件的分拣机类型 */}
                    {event.sortingMachineType && (
                        <div className="text-gray-600 text-sm">
                            {`${t('components.timeline.sortingMachineType')}: ${event.sortingMachineType}`}
                        </div>
                    )}
                    
                    {/* 下一步动作 */}
                    {event.nextAction && (
                        <div className="text-gray-600 text-sm">
                            {`${t('components.timeline.nextAction')}: ${getNextActionTextFromBackend(event.nextAction)}`}
                        </div>
                    )}
                    {/* 称重扫描事件的特殊展示 */}
                    {event.type === 'OrderEventDataWeightParcelScan' && (event.weightKg || event.sortingMachineCode) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {event.weightKg && (
                                <div>{`${t('components.timeline.weight')}: ${formatWeightKgToLb(event.weightKg)}`}</div>
                            )}
                            {event.sortingMachineCode && (
                                <div>{`${t('components.timeline.sortingMachineCode')}: ${event.sortingMachineCode}`}</div>
                            )}
                            {event.sortingMachineType && (
                                <div>{`${t('components.timeline.sortingMachineType')}: ${event.sortingMachineType}`}</div>
                            )}
                        </div>
                    )}
                    {/* 地址变更信息 */}
                    {event.type === 'OrderEventDataRecipientAddressChanged' && (event.oldRecipientAddress || event.newRecipientAddress) && (
                        <div className="mt-2 p-3 bg-gray-50 rounded-md border-l-4 border-blue-500">
                            {event.oldRecipientAddress && (
                                <div className="mb-2">
                                    <div className="font-medium text-gray-700 mb-1">{t('components.timeline.oldAddress')}:</div>
                                    <div className="text-gray-600 text-sm bg-white p-2 rounded border">
                                        {formatAddress(event.oldRecipientAddress)}
                                    </div>
                                </div>
                            )}
                            {event.newRecipientAddress && (
                                <div>
                                    <div className="font-medium text-gray-700 mb-1">{t('components.timeline.newAddress')}:</div>
                                    <div className="text-gray-600 text-sm bg-white p-2 rounded border">
                                        {formatAddress(event.newRecipientAddress)}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {/* 备注变更事件的特殊展示 */}
                    {event.type === 'OrderEventDataChangeNotes' && (event?.oldOrderNote || event?.newOrderNote) && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-md border-l-4 border-blue-500">
                            <div className="text-sm space-y-2">
                                {event?.oldOrderNote && (
                                    <div>
                                        <div className="font-medium text-gray-700 mb-1">{t('components.timeline.oldNote')}:</div>
                                        <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border">
                                            {getOrderNoteContent(event.oldOrderNote,currentLanguage)}
                                        </div>
                                    </div>
                                )}

                                {event?.newOrderNote && (
                                    <div>
                                        <div className="font-medium text-gray-700 mb-1">{t('components.timeline.newNote')}:</div>
                                        <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border">
                                            {getOrderNoteContent(event.newOrderNote, currentLanguage)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {/* POD审核修改事件的特殊展示 */}
                    {event.type === 'OrderEventDataPodReviewModified' && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-md border-l-4 border-purple-500">
                            <div className="text-sm space-y-2">
                                {/* 修改前状态 */}
                                <div>
                                    <div className="font-medium text-gray-700 mb-1">{t('components.timeline.previousStatus')}:</div>
                                    <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border">
                                        {event.previousStatus ? (() => {
                                            const statusMap = {
                                                'MANUAL_APPROVED': t('enum.podReviewStatus.MANUAL_APPROVED'),
                                                'MANUAL_DISAPPROVED': t('enum.podReviewStatus.MANUAL_DISAPPROVED'),
                                                'MANUAL_CORRECTED': t('enum.podReviewStatus.MANUAL_CORRECTED')
                                            };
                                            return statusMap[event.previousStatus as keyof typeof statusMap] || event.previousStatus;
                                        })() : '—'}
                                    </div>
                                </div>

                                {/* 修改后状态 */}
                                <div>
                                    <div className="font-medium text-gray-700 mb-1">{t('components.timeline.currentStatus')}:</div>
                                    <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border">
                                        {(() => {
                                            const statusMap = {
                                                'MANUAL_APPROVED': t('enum.podReviewStatus.MANUAL_APPROVED'),
                                                'MANUAL_DISAPPROVED': t('enum.podReviewStatus.MANUAL_DISAPPROVED'),
                                                'MANUAL_CORRECTED': t('enum.podReviewStatus.MANUAL_CORRECTED')
                                            };

                                            // 直接使用 newStatus 字段，添加空值检查
                                             if (event.newStatus) {
                                                 return statusMap[event.newStatus as keyof typeof statusMap] || event.newStatus;
                                            }
                                            return '—';
                                        })()}
                                    </div>
                                </div>

                                {/* 修改前理由 - 只有有理由时才显示 */}
                                {event.previousReviewNotes && event.previousReviewNotes.length > 0 && (
                                    <div>
                                        <div className="font-medium text-gray-700 mb-1">{t('components.timeline.previousReviewNotes')}:</div>
                                        <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border">
                                            {formatPodComment(event.previousReviewNotes)}
                                        </div>
                                    </div>
                                )}

                                {/* 修改后理由 */}
                                <div>
                                    <div className="font-medium text-gray-700 mb-1">{t('components.timeline.currentReviewNotes')}:</div>
                                    <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border">
                                        {event.podComment && event.podComment.length > 0 ? formatPodComment(event.podComment) : '—'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* 图片 */}
                    {event.imageS3UrlsJson && event.imageS3UrlsJson.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                            {event.imageS3UrlsJson.map((imageUrl, imgIndex) => (
                                <div 
                                    key={imgIndex} 
                                    className="w-16 h-16 border border-gray-300 rounded overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={() => handleImageClick(event.imageS3UrlsJson!, imgIndex)}
                                >
                                    <Image
                                        src={imageUrl}
                                        alt={`Event image ${imgIndex + 1}`}
                                        className="w-full h-full object-cover"
                                        fit="cover"
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ),
        }));
    }, [packageEvents, formatEventTitle, formatByInfo, getExceptionReasonText, getExceptionSubReasonText, getNextActionTextFromBackend, handleImageClick, t, deliveryMethodI18nConfig, i18n.language]);

    // 如果异常字典还在加载中，显示加载状态
    if (exceptionDictLoading) {
        return (
            <div className="bg-white p-4">
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={5} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={3} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={1} animated/>
                <Skeleton style={{width: '100%', height: '30%'}} animated/>
            </div>
        );
    }

    return (
        <>
            <div className="bg-white p-4">
                {timelineItems.length > 0 ? (
                    <div className="space-y-4">
                        {timelineItems.map((item, index) => (
                            <div key={item.key} className="flex">
                                {/* 时间轴线 */}
                                <div className="flex flex-col items-center mr-4">
                                    <div 
                                        className={`w-3 h-3 rounded-full ${
                                            item.color === 'purple' ? 'bg-purple-500' : 'bg-gray-400'
                                        }`}
                                    />
                                    {index < timelineItems.length - 1 && (
                                        <div className="w-0.5 h-8 bg-gray-300 mt-2" />
                                    )}
                                </div>
                                {/* 内容 */}
                                <div className="flex-1 pb-4">
                                    {item.children}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center text-gray-500 py-8">
                        {t('pages.dsp.packageDetail.noTrackingData')}
                    </div>
                )}
            </div>
            
            {/* 图片预览器 */}
            <ImageViewer.Multi
                images={currentImages}
                visible={imageViewerVisible}
                onClose={() => setImageViewerVisible(false)}
                defaultIndex={currentImageIndex}
                getContainer={null}
            />
        </>
    );
};

export default DspPackageTimeline;
